import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper
} from '@mui/material';
import { Search, Close, Delete as DeleteIcon, Edit as EditIcon, ArrowBack, ArrowForward, UploadFile } from '@mui/icons-material';
import dayjs from 'dayjs';
import _ from 'underscore';
import JBrowseService from '../services/jbrowse.service';

export default function NotesModal(props) {
  const {
    notes,
    notesIsOpen,
    setNotesIsOpen,
    navigateToLocation,
    setLoading,
    setBatchNotesModalIsOpen,
    selectedAssembly,
    currentUser,
    setCurrentNote,
    setEditNotesModalIsOpen,
    setNotes
  } = props;

  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter, reverse, then paginate
  const filteredNotes = notes
    .filter(note => note.note.toLowerCase().includes(query.toLowerCase()))
    .slice()
    .reverse();

  const totalPages = Math.ceil(filteredNotes.length / itemsPerPage);
  const paginatedData = filteredNotes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  function removeParenthesesContent(str) {
    return str.replace(/\s+\([^)]+\)$/, '');
  }

  const closeModal = () => {
    setNotesIsOpen(false);
  };

  const editNote = (note) => {
    setCurrentNote(note);
    setNotesIsOpen(false);
    setTimeout(() => {
      setEditNotesModalIsOpen(true);
    });
  };

  const deleteNote = (note) => {
    const userConfirmation = window.confirm('Are you sure you want to delete this note?');
    if (userConfirmation) {
      setLoading(true);
      const obj = {
        note: note,
        user: currentUser,
        reference: selectedAssembly
      };
      JBrowseService.deleteNotes(obj).then(
        () => {
          const notesObj = {
            idCompany: currentUser.Company_idCompany,
            username: currentUser.email,
            assembly: selectedAssembly
          };
          // Refresh notes
          JBrowseService.getNotes(notesObj).then(
            (response) => {
              const updatedNotes = [];
              _.each(response.result, function (value) {
                const transformed = {
                  ...value,
                  created_at: dayjs(value.created_at).format('MMMM D YYYY, h:mm:ss A')
                };
                updatedNotes.push(transformed);
              });
              setNotes(updatedNotes);
              setLoading(false);
            },
            () => {
              setLoading(false);
            }
          );
        },
        () => {
          setLoading(false);
        }
      );
    }
  };

  const showBatchNotes = () => {
    setNotesIsOpen(false);
    setTimeout(() => {
      setBatchNotesModalIsOpen(true);
    });
  };

  const jumpToLocation = (location) => {
    let loc = removeParenthesesContent(location);
    setLoading(true);
    navigateToLocation(loc);
    setTimeout(() => {
      setNotesIsOpen(false);
      setLoading(false);
    }, 1000);
  };

  return (
    <Dialog
      open={notesIsOpen}
      onClose={closeModal}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        elevation: 5,
        sx: { overflowY: 'visible', boxShadow: 10 }
      }}
      >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
        <Typography variant="h6">Notes <small>- {notes.length}</small></Typography>
        <IconButton onClick={closeModal}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {notes.length === 0 ? (
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="primary" gutterBottom>
              <strong>No Notes Exist</strong> – take some notes.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<UploadFile />}
              onClick={showBatchNotes}
            >
              Upload Batch Notes
            </Button>
          </Paper>
        ) : (
          <>
            {/* Search Field */}
            <TextField
              fullWidth
              label="Search Notes"
              variant="outlined"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ mt: 1, mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {!query ? <Search /> : (
                      <IconButton onClick={() => setQuery('')}>
                        <Close />
                      </IconButton>
                    )}
                  </InputAdornment>
                )
              }}
            />
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ backgroundColor: 'action.hover' }}>
                  <TableRow>
                    <TableCell>Jump</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Edit</TableCell>
                    <TableCell>Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((note, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => jumpToLocation(note.location)}
                          fullWidth
                        >
                          Jump
                        </Button>
                      </TableCell>
                      <TableCell>
                        {note.Public === 0 ? 'Public' : 'Private'}
                      </TableCell>
                      <TableCell>
                        {note.category}
                      </TableCell>
                      <TableCell>
                        {note.location}
                      </TableCell>
                      <TableCell>
                        {note.note}
                      </TableCell>
                      <TableCell>
                        {note.created_at}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          onClick={() => editNote(note)}
                          startIcon={<EditIcon />}
                        >
                          Edit
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="secondary"
                          fullWidth
                          onClick={() => deleteNote(note)}
                          startIcon={<DeleteIcon />}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            {filteredNotes.length > itemsPerPage && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                <div>
                  {currentPage > 1 && (
                    <Button
                      variant="contained"
                      startIcon={<ArrowBack />}
                      onClick={() => setCurrentPage(currentPage - 1)}
                      sx={{ mr: 1 }}
                    >
                      Previous
                    </Button>
                  )}
                </div>
                <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                  Page {currentPage} of {totalPages}
                </Typography>
                <div>
                  {currentPage < totalPages && (
                    <Button
                      variant="contained"
                      endIcon={<ArrowForward />}
                      onClick={() => setCurrentPage(currentPage + 1)}
                      sx={{ ml: 1 }}
                    >
                      Next
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={closeModal} variant="outlined">
          Close
        </Button>
        <Button
          onClick={showBatchNotes}
          variant="contained"
          startIcon={<UploadFile />}
        >
          Upload Batch Notes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
